/**
 * What a successful create (BON-628) turns into for the launcher — a
 * destination to navigate to, plus a toast or an error dialog. Pure, so every
 * row of the outcome table is easy to pin without `CreateBookingLauncherProvider`
 * growing past the repo's line cap.
 */
import type { ToastRequest } from '../../../shared/toast/toastContext';

import { formatHoldUntil, type CreateBookingSuccess } from './CreateBookingModalHelpers';

/** Long enough to read the hold time out loud on the phone — the default
 *  toast duration is tuned for a shorter confirmation. */
const RESERVATION_TOAST_MS = 8000;

export interface CreatedOutcomeEffects {
  navigateTo: string | null;
  toast?: ToastRequest;
  emailFailedFor?: string;
}

function compEffects(
  outcome: Extract<CreateBookingSuccess, { kind: 'comp' }>,
  canNavigate: boolean,
): CreatedOutcomeEffects {
  return {
    navigateTo: canNavigate ? `/admin/bookings/${encodeURIComponent(outcome.bookingId)}` : null,
    toast: {
      message: outcome.confirmationCode
        ? `Comp booking confirmed · ${outcome.confirmationCode}`
        : 'Comp booking confirmed',
    },
  };
}

function reservationEffects(
  outcome: Extract<CreateBookingSuccess, { kind: 'reservation' }>,
  canNavigate: boolean,
): CreatedOutcomeEffects {
  const navigateTo = canNavigate ? '/admin/bookings?tab=reservations' : null;

  if (outcome.duplicate) {
    // The first submit already sent the email — nothing here failed.
    return {
      navigateTo,
      toast: { message: 'Already reserved a moment ago — no second email sent.', tone: 'info' },
    };
  }

  if (!outcome.emailed) {
    return { navigateTo, emailFailedFor: outcome.email };
  }

  return {
    navigateTo,
    toast: {
      message: `Booking link emailed to ${outcome.email}. Seats held until ${formatHoldUntil(outcome.expiresAt)}.`,
      durationMs: RESERVATION_TOAST_MS,
    },
  };
}

export function resolveCreatedOutcome(
  outcome: CreateBookingSuccess,
  canNavigate: boolean,
): CreatedOutcomeEffects {
  return outcome.kind === 'comp'
    ? compEffects(outcome, canNavigate)
    : reservationEffects(outcome, canNavigate);
}
